import { useState, useEffect } from 'react'
import { Header } from '../components/layout/Header'

interface CostCenter {
  id: string
  name: string
  description: string
  budget_monthly: number
  spent_mtd: number
  budget_utilization: number
  forecast_eom: number
  trend: string
  change_percent: number
  top_service: string
  owner: string
}

interface CostAnomaly {
  id: string
  cost_center_name: string
  type: string
  severity: string
  description: string
  amount: number
  expected_amount: number
  deviation_percent: number
  detected_at: string
  status: string
  root_cause: string | null
}

interface OptimizationOpportunity {
  id: string
  cost_center_name: string
  type: string
  title: string
  description: string
  estimated_savings_monthly: number
  estimated_savings_annual: number
  confidence: number
  effort: string
  risk: string
  status: string
}

interface CostStats {
  total_cost_centers: number
  total_budget: number
  total_spent_mtd: number
  budget_utilization: number
  over_budget_centers: number
  total_anomalies: number
  unresolved_anomalies: number
  optimization_opportunities: number
  potential_savings_monthly: number
  realized_savings_monthly: number
}

const CostOptimizationPage = () => {
  const [costCenters, setCostCenters] = useState<CostCenter[]>([])
  const [anomalies, setAnomalies] = useState<CostAnomaly[]>([])
  const [opportunities, setOpportunities] = useState<OptimizationOpportunity[]>([])
  const [stats, setStats] = useState<CostStats | null>(null)
  const [loading, setLoading] = useState(true)
  const [activeTab, setActiveTab] = useState('overview')

  useEffect(() => {
    fetchData()
  }, [])

  const fetchData = async () => {
    try {
      const [centersRes, anomaliesRes, oppsRes, statsRes] = await Promise.all([
        fetch('/api/costopt/centers'),
        fetch('/api/costopt/anomalies'),
        fetch('/api/costopt/opportunities'),
        fetch('/api/costopt/statistics')
      ])

      setCostCenters(await centersRes.json())
      setAnomalies(await anomaliesRes.json())
      setOpportunities(await oppsRes.json())
      setStats(await statsRes.json())
    } catch (error) {
      console.error('Error fetching cost data:', error)
    } finally {
      setLoading(false)
    }
  }

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(amount)
  }

  const getTrendColor = (trend: string) => {
    switch (trend) {
      case 'increasing': return 'text-red-600'
      case 'decreasing': return 'text-green-600'
      default: return 'text-gray-600'
    }
  }

  const getTrendIcon = (trend: string) => {
    switch (trend) {
      case 'increasing': return '↑'
      case 'decreasing': return '↓'
      default: return '→'
    }
  }

  const getSeverityColor = (severity: string) => {
    switch (severity) {
      case 'high': return 'bg-red-100 text-red-800'
      case 'medium': return 'bg-yellow-100 text-yellow-800'
      case 'low': return 'bg-blue-100 text-blue-800'
      default: return 'bg-gray-100 text-gray-800'
    }
  }

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'identified': return 'bg-blue-100 text-blue-800'
      case 'in_progress': return 'bg-yellow-100 text-yellow-800'
      case 'implemented': return 'bg-green-100 text-green-800'
      case 'rejected': return 'bg-red-100 text-red-800'
      default: return 'bg-gray-100 text-gray-800'
    }
  }

  const getEffortColor = (effort: string) => {
    switch (effort) {
      case 'low': return 'bg-green-100 text-green-800'
      case 'medium': return 'bg-yellow-100 text-yellow-800'
      case 'high': return 'bg-red-100 text-red-800'
      default: return 'bg-gray-100 text-gray-800'
    }
  }

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50">
        <Header />
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
        </div>
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <Header />
      <main className="max-w-7xl mx-auto py-6 px-4 sm:px-6 lg:px-8">
        <div className="mb-6">
          <h1 className="text-2xl font-bold text-gray-900">Cost Optimization</h1>
          <p className="text-gray-600">Monitor spending, detect anomalies, and optimize costs</p>
        </div>

        {stats && (
          <div className="grid grid-cols-1 md:grid-cols-5 gap-4 mb-6">
            <div className="bg-white rounded-lg shadow p-4 border-l-4 border-blue-500">
              <p className="text-sm text-gray-500">Total Budget</p>
              <p className="text-2xl font-bold">{formatCurrency(stats.total_budget)}</p>
              <p className="text-xs text-gray-500">{stats.total_cost_centers} cost centers</p>
            </div>
            <div className="bg-white rounded-lg shadow p-4 border-l-4 border-green-500">
              <p className="text-sm text-gray-500">Spent MTD</p>
              <p className="text-2xl font-bold">{formatCurrency(stats.total_spent_mtd)}</p>
              <p className="text-xs text-gray-500">{stats.budget_utilization}% utilized</p>
            </div>
            <div className="bg-white rounded-lg shadow p-4 border-l-4 border-red-500">
              <p className="text-sm text-gray-500">Over Budget</p>
              <p className="text-2xl font-bold">{stats.over_budget_centers}</p>
              <p className="text-xs text-red-600">{stats.unresolved_anomalies} anomalies</p>
            </div>
            <div className="bg-white rounded-lg shadow p-4 border-l-4 border-purple-500">
              <p className="text-sm text-gray-500">Potential Savings</p>
              <p className="text-2xl font-bold text-purple-600">{formatCurrency(stats.potential_savings_monthly)}</p>
              <p className="text-xs text-gray-500">per month</p>
            </div>
            <div className="bg-white rounded-lg shadow p-4 border-l-4 border-green-600">
              <p className="text-sm text-gray-500">Realized Savings</p>
              <p className="text-2xl font-bold text-green-600">{formatCurrency(stats.realized_savings_monthly)}</p>
              <p className="text-xs text-gray-500">per month</p>
            </div>
          </div>
        )}

        <div className="bg-white rounded-lg shadow">
          <div className="border-b border-gray-200">
            <nav className="flex -mb-px">
              {['overview', 'anomalies', 'opportunities'].map((tab) => (
                <button
                  key={tab}
                  onClick={() => setActiveTab(tab)}
                  className={`py-4 px-6 text-sm font-medium capitalize ${
                    activeTab === tab
                      ? 'border-b-2 border-blue-500 text-blue-600'
                      : 'text-gray-500 hover:text-gray-700'
                  }`}
                >
                  {tab}
                </button>
              ))}
            </nav>
          </div>

          <div className="p-4">
            {activeTab === 'overview' && (
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Cost Center</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Budget</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Spent MTD</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Utilization</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Forecast EOM</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Trend</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Top Service</th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {costCenters.map((center) => (
                      <tr key={center.id} className={`hover:bg-gray-50 ${center.budget_utilization > 100 ? 'bg-red-50' : ''}`}>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div>
                            <p className="font-medium">{center.name}</p>
                            <p className="text-xs text-gray-500">{center.owner}</p>
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">{formatCurrency(center.budget_monthly)}</td>
                        <td className="px-6 py-4 whitespace-nowrap">{formatCurrency(center.spent_mtd)}</td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="flex items-center gap-2">
                            <div className="w-20 bg-gray-200 rounded-full h-2">
                              <div
                                className={`h-2 rounded-full ${center.budget_utilization > 100 ? 'bg-red-600' : center.budget_utilization > 80 ? 'bg-yellow-500' : 'bg-green-500'}`}
                                style={{ width: `${Math.min(center.budget_utilization, 100)}%` }}
                              ></div>
                            </div>
                            <span className={`text-sm ${center.budget_utilization > 100 ? 'text-red-600 font-medium' : ''}`}>
                              {center.budget_utilization}%
                            </span>
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">{formatCurrency(center.forecast_eom)}</td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <span className={`font-medium ${getTrendColor(center.trend)}`}>
                            {getTrendIcon(center.trend)} {center.change_percent}%
                          </span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm">{center.top_service}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}

            {activeTab === 'anomalies' && (
              <div className="space-y-4">
                {anomalies.filter(a => a.status !== 'resolved').slice(0, 10).map((anomaly) => (
                  <div key={anomaly.id} className="border rounded-lg p-4">
                    <div className="flex justify-between items-start">
                      <div>
                        <div className="flex items-center gap-2">
                          <span className={`px-2 py-1 rounded text-xs ${getSeverityColor(anomaly.severity)}`}>
                            {anomaly.severity}
                          </span>
                          <span className="px-2 py-1 rounded text-xs bg-gray-100 text-gray-800">
                            {anomaly.type.replace('_', ' ')}
                          </span>
                        </div>
                        <h3 className="font-medium mt-2">{anomaly.description}</h3>
                        <p className="text-sm text-gray-500 mt-1">Cost Center: {anomaly.cost_center_name}</p>
                      </div>
                      <div className="text-right">
                        <p className="text-lg font-bold text-red-600">{formatCurrency(anomaly.amount)}</p>
                        <p className="text-sm text-gray-500">Expected: {formatCurrency(anomaly.expected_amount)}</p>
                        <p className="text-sm text-red-600">+{anomaly.deviation_percent}% deviation</p>
                      </div>
                    </div>
                    <div className="mt-4 flex justify-between items-center text-xs text-gray-500">
                      <span>Detected: {anomaly.detected_at}</span>
                      {anomaly.root_cause && <span>Root Cause: {anomaly.root_cause}</span>}
                    </div>
                  </div>
                ))}
              </div>
            )}

            {activeTab === 'opportunities' && (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {opportunities.filter(o => o.status === 'identified').slice(0, 10).map((opp) => (
                  <div key={opp.id} className="border rounded-lg p-4">
                    <div className="flex justify-between items-start">
                      <div>
                        <span className="px-2 py-1 rounded text-xs bg-purple-100 text-purple-800">
                          {opp.type.replace('_', ' ')}
                        </span>
                        <h3 className="font-medium mt-2">{opp.title}</h3>
                        <p className="text-sm text-gray-600 mt-1">{opp.description}</p>
                      </div>
                    </div>
                    <div className="mt-4 grid grid-cols-3 gap-4 text-center">
                      <div>
                        <p className="text-lg font-bold text-green-600">{formatCurrency(opp.estimated_savings_monthly)}</p>
                        <p className="text-xs text-gray-500">Monthly Savings</p>
                      </div>
                      <div>
                        <p className="text-lg font-bold">{(opp.confidence * 100).toFixed(0)}%</p>
                        <p className="text-xs text-gray-500">Confidence</p>
                      </div>
                      <div>
                        <span className={`px-2 py-1 rounded text-xs ${getEffortColor(opp.effort)}`}>
                          {opp.effort} effort
                        </span>
                      </div>
                    </div>
                    <div className="mt-4 flex justify-between items-center text-sm">
                      <span className="text-gray-500">{opp.cost_center_name}</span>
                      <span className={`px-2 py-1 rounded text-xs ${getStatusColor(opp.status)}`}>
                        {opp.status}
                      </span>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </main>
    </div>
  )
}

export default CostOptimizationPage
